import {Edit} from "@mui/icons-material";
import {useAuthenticator} from "@aws-amplify/ui-react"
import {generateClient} from 'aws-amplify/api';
import {GraphQLResult} from '@aws-amplify/api-graphql'
import {ListParkReviewsQuery, ParkReview} from 'API'
import {listParkReviews} from "graphql/queries"
import {useEffect, useState} from "react"
import {Button, CardContent, Divider, Paper, Rating, Skeleton, Stack, Typography} from "@mui/material"
import {CreateReview} from "./CreateReview";
import './ReviewSection.css'
import {UserAvatar} from "../user/UserAvatar";
import {ReviewsLocked} from "./ReviewsLocked";
import {useRequireAuth} from "../../services/auth/useRequireAuth";


type ReviewSectionProps = {
  parkId: string, name: string
}


export const ReviewSection = ({parkId, name}: ReviewSectionProps) => {
  const {user} = useAuthenticator((context) => [context.user])
  const {authState, requireAuth, signInDialog, promptSignIn} = useRequireAuth()
  const client = generateClient()
  const [reviews, setReviews] = useState<[] | ParkReview[]>([])
  const [usersReview, setUsersReview] = useState<ParkReview>()
  const [createReview, setCreateReview] = useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    // Reviews are member-only, so a guest never asks for them. Holding off
    // while auth is still resolving keeps a signed-in member from seeing the
    // locked panel flash before their session lands.
    if (authState !== "signedIn") {
      setReviews([])
      setUsersReview(undefined)
      setLoading(authState === "loading")
      return
    }

    setLoading(true)
    const reviewsPromise = client.graphql({
      query: listParkReviews, variables: {
        filter: {
          parkId: {
            eq: parkId
          }
        }
      }
    }) as Promise<GraphQLResult<ListParkReviewsQuery>>

    reviewsPromise.then((res) => {
      const items = res.data?.listParkReviews?.items ?? []

      setReviews(items.filter((item): item is ParkReview => Boolean(item)))
      setUsersReview(items.find(item => item?.owner === user?.username) || undefined)
    }).finally(() => setLoading(false))
  }, [authState, user, createReview, parkId])

  return (<>
      <Typography variant="h6" component="div" sx={{mt: 6}}>Reviews</Typography>
      {authState === "guest" && <ReviewsLocked onSignIn={promptSignIn}/>}
      {authState !== "guest" && <>
        {loading && <Stack spacing={1.5}>
          {[0, 1].map((i) => <Skeleton key={i} variant="rounded" height={120}/>)}
        </Stack>}
        {!loading && reviews.length === 0 && <Typography variant="body2" color="text.secondary">
          No reviews yet. Be the first to leave one.
        </Typography>}
        {!loading && reviews.map(review => {
          return <Paper
            key={review.id}
            variant="outlined"
          >
            <CardContent>
              <div className="ReviewSection-header">
                <div className="ReviewSection-user">
                  <UserAvatar userName={review.owner}/>
                  <Typography variant="subtitle1" component="div">{review.owner || "ANNON"}</Typography>
                </div>
                {usersReview?.id === review.id && (<Button
                    variant="contained"
                    onClick={() => setCreateReview(true)}
                  >
                    <Edit/>
                  </Button>)}
              </div>
              <div className="ReviewSection-review">
                <Rating name="rating" value={review.rating} readOnly/>
                <Typography variant="subtitle1" component="div" textTransform={'capitalize'}
                            className="ReviewSection-review-capitalize-first-letter">{review.title}</Typography>
                <Typography variant="body1" component="div"
                            className="ReviewSection-review-capitalize-first-letter">{review.description}</Typography>
              </div>
            </CardContent>
          </Paper>
        })}

        {!loading && !usersReview && <Button
            variant="contained"
            size={'large'} onClick={() => requireAuth(() => setCreateReview(true))}>Write a review
        </Button>}
      </>}
      <Divider/>
      {createReview && <CreateReview parkId={parkId} name={name} review={usersReview} onClose={() => {
        setCreateReview(false)
      }}/>}
      {signInDialog}
    </>)
}
